import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Layers, RefreshCw, TrendingUp, CalendarDays, Palette, Brush, X, ArrowRight } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";
import { SERVICES } from "../data/content";

const ICONS = {
  layers: Layers,
  refresh: RefreshCw,
  trending: TrendingUp,
  calendar: CalendarDays,
  palette: Palette,
  brush: Brush,
};

export default function Services() {
  const [active, setActive] = useState(null);

  return (
    <section id="services" className="py-20 sm:py-28 lg:py-32 scroll-mt-16" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <SectionHeading
          eyebrow="Services"
          title="Six ways her experience works for you"
          subtitle="From SKU economics to India-specific colour science — each engagement is delivered the way global leaders run design."
        />

        <div className="mt-12 sm:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.id} delay={(i % 3) * 0.08}>
                <button
                  data-testid={`service-card-item-${s.id}`}
                  onClick={() => setActive(s)}
                  className="group text-left w-full h-full bg-white rounded-3xl border border-[#e5e5e7] p-7 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 flex flex-col gap-5"
                >
                  <span className="w-11 h-11 rounded-2xl bg-[#f5f5f7] border border-[#e5e5e7] flex items-center justify-center text-[#0066cc] group-hover:bg-[#0066cc] group-hover:text-white transition-colors duration-300">
                    <Icon size={20} strokeWidth={1.8} />
                  </span>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold tracking-tight text-[#1d1d1f]">{s.title}</h3>
                    <p className="mt-1.5 text-sm text-[#0066cc] font-medium">{s.tagline}</p>
                    <p className="mt-3 text-[15px] text-[#86868b] leading-relaxed">{s.problem}</p>
                  </div>
                  <span className="flex items-center gap-1.5 text-sm font-medium text-[#1d1d1f]">
                    How she solves it
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 60, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.5, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full max-w-xl rounded-t-3xl sm:rounded-3xl p-8 sm:p-10 shadow-2xl"
              data-testid={`service-modal-open-${active.id}`}
            >
              <div className="flex items-start justify-between gap-6">
                <div>
                  <h3 className="text-2xl font-semibold tracking-tight">{active.title}</h3>
                  <p className="mt-1 text-sm text-[#0066cc] font-medium">{active.tagline}</p>
                </div>
                <button
                  onClick={() => setActive(null)}
                  aria-label="Close"
                  className="p-2 rounded-full hover:bg-[#f5f5f7] transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="mt-8 grid gap-5">
                <div className="rounded-2xl bg-[#faf0f0] border border-[#f0dddd] p-5">
                  <p className="text-xs uppercase tracking-[0.16em] font-semibold text-[#b3543f] mb-2">The problem</p>
                  <p className="text-[15px] text-[#1d1d1f]/80 leading-relaxed">{active.problem}</p>
                </div>
                <div className="rounded-2xl bg-[#eef6ee] border border-[#dcead c] p-5" style={{ borderColor: "#dceadc" }}>
                  <p className="text-xs uppercase tracking-[0.16em] font-semibold text-[#3d7a3d] mb-2">Her solution</p>
                  <p className="text-[15px] text-[#1d1d1f]/80 leading-relaxed">{active.solution}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
